// Single source of truth for the backend API base URL — resolved
// automatically from the domain the frontend is being viewed on, so ONE
// frontend deployment can serve multiple stations/clients safely.
//
// To add another private station later: add one more entry to API_MAP and
// point its domain at this same frontend container in Nginx Proxy Manager.
// No rebuild/redeploy of the frontend is needed for new domains that map to
// an EXISTING backend pattern — only if the mapping table itself changes.

const API_MAP = {
  'central.fms.tiger4pump.com':         'https://server.fms.tiger4pump.com',
  'central.private.fms.tiger4pump.com': 'https://private.server.fms.tiger4pump.com',

  // Local/dev fallback — adjust as needed while testing on localhost.
  'localhost':  'https://server.fms.tiger4pump.com',
};

const host = window.location.hostname;

// ---------------------------------------------------------------------
// Runtime override for local testing: lets you flip which backend
// localhost talks to WITHOUT a rebuild, by visiting a URL once.
//
//   http://localhost:3000/?api=central   -> talks to server.fms.tiger4pump.com
//   http://localhost:3000/?api=private   -> talks to private.server.fms.tiger4pump.com
//   http://localhost:3000/?api=https://some.other.host   -> any explicit URL
//   http://localhost:3000/?api=clear     -> removes the override, back to API_MAP
//
// The choice is written to localStorage so it survives reloads and new tabs
// until you explicitly change or clear it - you don't need the query param
// on every visit, just the first time you want to switch.
//
// Named shortcuts so you don't have to remember/retype the full backend
// URLs each time you toggle.
const API_OVERRIDE_SHORTCUTS = {
  central: 'https://server.fms.tiger4pump.com',
  private: 'https://private.server.fms.tiger4pump.com',
};

const OVERRIDE_KEY = 'fms_api_base_override';

const params = new URLSearchParams(window.location.search);
if (params.has('api')) {
  const requested = params.get('api');
  if (requested === 'clear' || requested === '') {
    window.localStorage.removeItem(OVERRIDE_KEY);
  } else {
    const resolved = API_OVERRIDE_SHORTCUTS[requested] || requested;
    window.localStorage.setItem(OVERRIDE_KEY, resolved);
  }
}

const runtimeOverride = window.localStorage.getItem(OVERRIDE_KEY);

// Only ever apply the runtime override on localhost/dev hosts - this is a
// developer convenience, not something that should ever silently redirect
// a real deployed station's traffic to a different backend because of a
// stray localStorage value.
//
// "Dev host" also covers private LAN IPs (192.168.x.x, 10.x.x.x,
// 172.16-31.x.x) so a colleague testing over the local network at e.g.
// http://192.168.216.4:3000/?api=private gets the override too - a real
// production domain (central.fms.tiger4pump.com etc.) never matches this,
// since those are DNS names, not raw IPs.
const isPrivateLanIp = (h) =>
  /^192\.168\.\d{1,3}\.\d{1,3}$/.test(h) ||
  /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h) ||
  /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(h);

const isDevHost = host === 'localhost' || host === '127.0.0.1' || isPrivateLanIp(host);

export const API_BASE =
  process.env.REACT_APP_API_BASE ||             // explicit build-time override always wins
  (isDevHost && runtimeOverride) ||              // then a runtime toggle, but only on localhost
  API_MAP[host] ||                               // otherwise resolve from the current domain
  API_MAP['central.fms.tiger4pump.com'];         // safe default if hostname isn't recognized

if (!API_MAP[host] && !process.env.REACT_APP_API_BASE && !(isDevHost && runtimeOverride)) {
  // eslint-disable-next-line no-console
  console.warn(
    `[config] Unrecognized hostname "${host}" — falling back to the default API_BASE. ` +
    `Add it to API_MAP in src/config.js if this is a new deployment.`
  );
}

if (isDevHost) {
  // eslint-disable-next-line no-console
  console.info(
    `[config] API_BASE = ${API_BASE}` +
    (runtimeOverride ? ' (localStorage override active)' : ' (default)') +
    ` — switch with ?api=central, ?api=private, or ?api=clear`
  );
}